import { ChangeEvent, ReactNode, use, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PracticeVideoPart } from '../../../../common/src/model.ts';
import {
  practiceVideoPartLabels,
  practiceVideoParts,
  youTubeThumbnailUrl,
  youTubeWatchUrl,
} from '../../../../common/src/youtube.ts';
import { getUpdateAppUrl } from '../../pages/learn/learn_update_app_screen.tsx';
import { NetworkError } from '../../util/network.ts';
import { QuarterNote } from '../../util/shared.tsx';
import { mixerLandscapeContainerQuery } from '../player_container_queries.ts';
import {
  SliderBase,
  SliderButtonAndRangeWrapper,
  sliderButtonWidth,
  SliderButtonWrapper,
  SliderLabel,
  ThumbSize,
} from '../player_util.tsx';
import { SequencerContext } from '../sequencer.tsx';
import { stopwatchThumbStyles } from '../tempo_sliders/tempo_slider.tsx';
import { usePracticeVideoChannelName } from './practice_video_channel.ts';
import { PracticeVideoContext, songSources } from './practice_video_context.tsx';
import { PlayerSource, SourceSelector, useSelectPlayerSource } from './source_selector.tsx';
import { YouTubeMark } from './youtube_mark.tsx';

// the speed slider's stops; the thumb snaps to them
const speedStops = [0.5, 0.75, 1, 1.25, 1.5];
const minSpeed = speedStops[0];
const maxSpeed = speedStops[speedStops.length - 1];
const speedStep = 0.25;

/**
 * The mixer card in video mode (GitHub issue #4041): the practice video takes the leftover height, with the part
 * picker, the source oval, the speed slider and the timeline under it (beside it in landscape). The transport
 * is the player's own, below the card. `timeline` is the mixer's location slider, time based here.
 */
export function PracticeVideoMode({ timeline }: { timeline: ReactNode }) {
  return (
    <VideoModeLayout>
      <VideoArea />
      <VideoControls>
        <PartPicker />
        <SourceSelector />
        <SpeedSlider />
        {timeline}
      </VideoControls>
    </VideoModeLayout>
  );
}

function VideoArea() {
  const { videoId, selectedPart } = use(PracticeVideoContext);
  const { readyToPlay, networkError, practiceVideoHost } = use(SequencerContext);
  const channelName = usePracticeVideoChannelName(videoId);
  const hostSlotRef = useRef<HTMLDivElement>(null);

  // The sequencer's YouTube player renders into its host; the host lives here while video mode is on. Moving the
  // host reloads the video, so it is appended once and stays in this one slot.
  useEffect(() => {
    const slot = hostSlotRef.current;
    if (!slot || !practiceVideoHost) {
      return;
    }
    slot.appendChild(practiceVideoHost);
    return () => practiceVideoHost.remove();
  }, [practiceVideoHost]);

  // the store app is too old to embed the video (GitHub issue #4058): the only placeholder with a button
  const needsAppUpdate = networkError === NetworkError.VideoNeedsAppUpdate;
  const placeholderMessage = needsAppUpdate
    ? 'Update the app to watch practice videos here.'
    : networkError === NetworkError.VideoUnavailable
      ? "This video can't be played here. Open it in YouTube."
      : networkError === NetworkError.VideoLoadFailed
        ? 'Practice videos need an internet connection.'
        : !readyToPlay
          ? 'Loading video…'
          : undefined;

  return (
    <VideoAreaWrapper data-testid={practiceVideoAreaTestId}>
      <VideoHostSlot ref={hostSlotRef} />
      {videoId && placeholderMessage && (
        <VideoPlaceholder>
          <VideoThumbnail src={youTubeThumbnailUrl(videoId)} alt="" />
          <VideoPlaceholderText>
            <VideoPlaceholderMessage role="status">{placeholderMessage}</VideoPlaceholderMessage>
            {needsAppUpdate && (
              <UpdateAppButton
                type="button"
                onClick={() => {
                  window.location.href = getUpdateAppUrl();
                }}
              >
                Update the app
              </UpdateAppButton>
            )}
          </VideoPlaceholderText>
        </VideoPlaceholder>
      )}
      {selectedPart && (
        <VideoBadge>
          <YouTubeMark width={14} height={10} />
          {practiceVideoPartLabels[selectedPart]}
          {channelName && ` · ${channelName}`}
        </VideoBadge>
      )}
      {videoId && (
        <OpenInYouTube href={youTubeWatchUrl(videoId)} target="_blank" rel="noopener noreferrer">
          Open in YouTube
          <svg
            aria-hidden="true"
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 17L17 7M9 7h8v8" />
          </svg>
        </OpenInYouTube>
      )}
    </VideoAreaWrapper>
  );
}

const practiceVideoAreaTestId = 'practice-video-area';

// one button per part with a video, plus All for a whole-song video; a song with only a whole-song video gets a
// way back to the app's own parts instead
function PartPicker() {
  const { practiceVideos, selectedPart, setSelectedPart } = use(PracticeVideoContext);
  const { hymn } = use(SequencerContext);
  const { hasPiano, hasVocals } = songSources(hymn);
  const selectSource = useSelectPlayerSource();
  const parts = practiceVideoParts.filter((part) => practiceVideos?.[part]);

  if (parts.length === 0) {
    const partsSource = hasPiano ? PlayerSource.Piano : hasVocals ? PlayerSource.Vocals : undefined;
    return (
      <ControlRow $spaceBetween={true}>
        <WholeSongLabel>
          <PeopleIcon />
          All parts, one video
        </WholeSongLabel>
        {partsSource && (
          <PartButton type="button" $selected={false} $fit={true} onClick={() => selectSource(partsSource)}>
            {partsSource === PlayerSource.Piano ? 'Parts on piano' : 'Parts on vocals'}
          </PartButton>
        )}
      </ControlRow>
    );
  }

  const choices: PracticeVideoPart[] = practiceVideos?.all ? [...parts, 'all'] : parts;
  return (
    <ControlRow role="group" aria-label="Part">
      {choices.map((part) => (
        <PartButton
          key={part}
          type="button"
          aria-pressed={part === selectedPart}
          $selected={part === selectedPart}
          onClick={() => setSelectedPart(part)}
        >
          {part === 'all' ? 'All' : practiceVideoPartLabels[part]}
        </PartButton>
      ))}
    </ControlRow>
  );
}

function PeopleIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 20a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM7 20a3 3 0 1 0-6 0 3 3 0 0 0 6 0zM23 20a3 3 0 1 0-6 0 3 3 0 0 0 6 0zM12 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
    </svg>
  );
}

function formatSpeed(speed: number): string {
  return `${speed}x`;
}

// where a stop sits on the track: under the thumb's centre when the thumb is on it
function speedStopPosition(speed: number): string {
  const fraction = (speed - minSpeed) / (maxSpeed - minSpeed);
  return `calc(${ThumbSize} / 2 + (100% - ${ThumbSize}) * ${fraction})`;
}

function SpeedSlider() {
  const { playbackRate, setPlaybackRate } = use(PracticeVideoContext);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.currentTarget.value);
      if (!Number.isFinite(value)) {
        return;
      }
      // the nearest stop
      setPlaybackRate(Math.min(maxSpeed, Math.max(minSpeed, Math.round(value / speedStep) * speedStep)));
    },
    [setPlaybackRate],
  );

  return (
    <div>
      <SliderButtonAndRangeWrapper>
        <SliderButtonWrapper>
          <SpeedLabel>
            <QuarterNote />
            <SliderLabel>=</SliderLabel>
            <SliderLabel>{formatSpeed(playbackRate)}</SliderLabel>
          </SpeedLabel>
        </SliderButtonWrapper>
        <SpeedTrack>
          <SpeedTrackLine />
          {speedStops.map((stop) => (
            <SpeedStop key={stop} style={{ left: speedStopPosition(stop) }} />
          ))}
          <SpeedRange
            aria-label="Speed"
            aria-valuetext={formatSpeed(playbackRate)}
            min={minSpeed}
            max={maxSpeed}
            step={speedStep}
            value={playbackRate}
            onChange={onChange}
          />
        </SpeedTrack>
      </SliderButtonAndRangeWrapper>
      <SpeedStopLabels aria-hidden="true">
        <SpeedStopLabelTrack>
          {speedStops.map((stop) => (
            <SpeedStopLabel key={stop} style={{ left: speedStopPosition(stop) }}>
              {formatSpeed(stop)}
            </SpeedStopLabel>
          ))}
        </SpeedStopLabelTrack>
      </SpeedStopLabels>
    </div>
  );
}

// a flex child of the mixer card, which grows it to the card's full height
const VideoModeLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;

  @container ${mixerLandscapeContainerQuery} {
    flex-direction: row;
  }
`;

const VideoAreaWrapper = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 120px;
  min-width: 0;
  overflow: hidden;
  /* the video's own letterbox: black in both themes */
  background-color: black;
`;

const VideoHostSlot = styled.div`
  position: absolute;
  inset: 0;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
`;

const VideoThumbnail = styled.img`
  display: block;
  width: 100%;
  max-height: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  opacity: 0.5;
`;

const VideoPlaceholderText = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const VideoPlaceholderMessage = styled.div`
  text-align: center;
  color: white;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  text-shadow: 0 1px 4px rgb(0 0 0 / 0.8);
`;

// the way out of an iOS store app too old to embed the video (GitHub issue #4058)
const UpdateAppButton = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 15px;
  background-color: white;
  color: black;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

const videoOverlayBackground = 'rgb(255 255 255 / 0.12)';

const VideoBadge = styled.div`
  position: absolute;
  left: 12px;
  top: 12px;
  max-width: calc(100% - 24px);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: ${videoOverlayBackground};
  color: white;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  svg {
    flex-shrink: 0;
  }
`;

const OpenInYouTube = styled.a`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  border-radius: 13px;
  background-color: ${videoOverlayBackground};
  color: white;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  text-decoration: none;

  &:hover,
  &:visited {
    color: white;
  }
`;

const VideoControls = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 18px 0 18px;
  background-color: var(--color-background);

  @container ${mixerLandscapeContainerQuery} {
    width: 40%;
    min-width: 300px;
    max-width: 420px;
    padding: 8px 18px;
    justify-content: center;
    overflow-y: auto;
  }
`;

const ControlRow = styled.div<{ $spaceBetween?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$spaceBetween ? 'space-between' : 'stretch')};
  gap: 6px;
  min-height: 44px;
`;

const PartButton = styled.button<{ $selected: boolean; $fit?: boolean }>`
  flex: ${(props) => (props.$fit ? '0 0 auto' : '1 1 0')};
  min-width: 0;
  height: 34px;
  padding: 0 ${(props) => (props.$fit ? '10px' : '2px')};
  border: 1px solid ${(props) => (props.$selected ? 'var(--color-play-button)' : 'var(--color-field-border)')};
  border-radius: 6px;
  background-color: ${(props) => (props.$selected ? 'var(--color-play-button)' : 'var(--color-background)')};
  color: ${(props) => (props.$selected ? 'var(--color-play-button-icon)' : 'var(--color-text)')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const WholeSongLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 13px;
`;

const SpeedLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 6px;
`;

const SpeedTrack = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

// the range input's own track is clear, so the line and its stops are drawn behind it, under the thumb
const SpeedTrackLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 8.4px;
  transform: translateY(-50%);
  background: var(--color-slider);
`;

const SpeedStop = styled.div`
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  border: 2px solid var(--color-slider);
  border-radius: 50%;
  background-color: var(--color-background);
`;

const SpeedRange = styled(SliderBase)`
  position: relative;
  background: transparent;

  &::-webkit-slider-runnable-track {
    background: transparent;
  }

  ${stopwatchThumbStyles}
`;

const SpeedStopLabels = styled.div`
  display: flex;
  gap: 10px;
  margin-top: -8px;

  &::before {
    content: '';
    flex-shrink: 0;
    width: ${sliderButtonWidth};
  }
`;

const SpeedStopLabelTrack = styled.div`
  position: relative;
  flex: 1;
  height: 16px;
`;

const SpeedStopLabel = styled.span`
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 11px;
  color: var(--color-text-label);
  white-space: nowrap;
`;
